import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { RoundedBox, Text } from '@react-three/drei';
import * as THREE from 'three';
import { CHARACTER_FACE_COLORS } from './constants';
import type { CharacterProp } from '@/types/props';

interface ShowcaseCharacterProps {
  characterId: string;
  position: [number, number, number];
  rotating?: boolean;
  expression?: 'neutral' | 'happy' | 'shocked';
  props?: CharacterProp[];
  label?: string;
}

// 3D prop rendering on character
function PropMesh3D({ prop, charSize }: { prop: CharacterProp; charSize: number }) {
  const meshRef = useRef<THREE.Mesh>(null);
  const { color, secondaryColor, glowColor } = prop.model;
  const half = charSize / 2;

  useFrame(({ clock }) => {
    if (!meshRef.current) return;
    const t = clock.getElapsedTime();
    // Subtle float/pulse
    if (prop.slot === 'head') {
      meshRef.current.position.y = half + charSize * 0.35 + Math.sin(t * 2) * 0.015;
    }
  });

  const pos: [number, number, number] = (() => {
    switch (prop.slot) {
      case 'head': return [0, half + charSize * 0.35, 0];
      case 'hand': return [half + charSize * 0.15, -charSize * 0.1, 0];
      case 'back': return [0, 0, -half - charSize * 0.1];
      case 'face': return [0, charSize * 0.05, half + 0.02];
      case 'hair': return [0, half + charSize * 0.2, charSize * 0.05];
      case 'accessory':
      default: return [0, -half - charSize * 0.1, 0];
    }
  })();

  // Different shapes per slot
  return (
    <group position={pos}>
      <mesh ref={meshRef} castShadow>
        {prop.slot === 'head' ? (
          <coneGeometry args={[charSize * 0.3, charSize * 0.5, 6]} />
        ) : prop.slot === 'hand' ? (
          <cylinderGeometry args={[charSize * 0.04, charSize * 0.04, charSize * 0.5, 8]} />
        ) : prop.slot === 'back' ? (
          <boxGeometry args={[charSize * 0.6, charSize * 0.7, charSize * 0.15]} />
        ) : prop.slot === 'face' ? (
          <boxGeometry args={[charSize * 0.6, charSize * 0.15, 0.02]} />
        ) : prop.slot === 'hair' ? (
          <sphereGeometry args={[charSize * 0.35, 12, 12]} />
        ) : (
          <sphereGeometry args={[charSize * 0.12, 8, 8]} />
        )}
        <meshStandardMaterial
          color={color}
          emissive={glowColor || color}
          emissiveIntensity={0.2}
          metalness={0.3}
          roughness={0.4}
        />
      </mesh>
      {secondaryColor && prop.slot === 'head' && (
        <mesh position={[0, charSize * 0.2, charSize * 0.1]}>
          <sphereGeometry args={[charSize * 0.06, 6, 6]} />
          <meshStandardMaterial color={secondaryColor} emissive={secondaryColor} emissiveIntensity={0.5} />
        </mesh>
      )}
      {glowColor && (
        <pointLight color={glowColor} intensity={0.2} distance={1} position={[0, 0.1, 0]} />
      )}
    </group>
  );
}

export default function ShowcaseCharacter3D({
  characterId,
  position,
  rotating = false,
  expression = 'neutral',
  props = [],
  label,
}: ShowcaseCharacterProps) {
  const groupRef = useRef<THREE.Group>(null);
  const blinkTimer = useRef(Math.random() * 5);
  const isBlinking = useRef(false);
  const leftEyeRef = useRef<THREE.Mesh>(null);
  const rightEyeRef = useRef<THREE.Mesh>(null);
  const leftPupilRef = useRef<THREE.Mesh>(null);
  const rightPupilRef = useRef<THREE.Mesh>(null);
  const leftShineRef = useRef<THREE.Mesh>(null);
  const rightShineRef = useRef<THREE.Mesh>(null);

  const config = CHARACTER_FACE_COLORS[characterId] || CHARACTER_FACE_COLORS.default_hero;
  const charSize = 0.55 * config.scale;
  const half = charSize / 2;

  // Eye dimensions (flat, flush on face)
  const eyeW = charSize * 0.22 * config.eyeSize;
  const eyeH = charSize * 0.26 * config.eyeSize;
  const pupilW = eyeW * 0.5;
  const pupilH = eyeH * 0.55;
  const shineR = eyeW * 0.2;
  const eyeSpacing = charSize * 0.2 * config.eyeGap;
  const eyeY = charSize * 0.1;
  const faceZ = half + 0.005;
  const mouthW = charSize * 0.25 * config.mouthWidth;

  // Mouth shape (flat ShapeGeometry)
  const mouthGeometry = useMemo(() => {
    const shape = new THREE.Shape();
    const w = mouthW;
    const h = expression === 'happy' ? charSize * 0.1 : expression === 'shocked' ? charSize * 0.12 : charSize * 0.06;
    if (expression === 'shocked') {
      const r = w * 0.4;
      shape.absarc(0, 0, r, 0, Math.PI * 2, false);
    } else {
      shape.moveTo(-w / 2, 0);
      shape.quadraticCurveTo(0, expression === 'happy' ? -h * 2.5 : -h * 2, w / 2, 0);
      shape.quadraticCurveTo(0, expression === 'happy' ? -h * 0.2 : -h * 0.5, -w / 2, 0);
    }
    return new THREE.ShapeGeometry(shape);
  }, [mouthW, charSize, expression]);

  useFrame((_, delta) => {
    if (!groupRef.current) return;

    // Idle breathing
    const t = performance.now() / 1000;
    const breathe = 1 + Math.sin(t * 2) * 0.02;
    groupRef.current.children[0]?.scale.set(1, breathe, 1);

    // Rotation
    if (rotating) {
      groupRef.current.rotation.y += delta * 0.5;
    }

    // Blinking
    blinkTimer.current -= delta;
    if (blinkTimer.current <= 0) {
      if (!isBlinking.current) {
        isBlinking.current = true;
        blinkTimer.current = 0.12;
      } else {
        isBlinking.current = false;
        blinkTimer.current = 3 + Math.random() * 4;
      }
    }
    const eyeScaleY = isBlinking.current ? 0.1 : (expression === 'shocked' ? 1.3 : 1);
    [leftEyeRef, rightEyeRef, leftPupilRef, rightPupilRef, leftShineRef, rightShineRef].forEach(ref => {
      if (ref.current) ref.current.scale.y = eyeScaleY;
    });
  });

  return (
    <group position={position}>
      <group ref={groupRef}>
        {/* Body - 6 separate faces for proper coloring */}
        <RoundedBox
          args={[charSize, charSize, charSize]}
          radius={charSize * 0.22}
          smoothness={4}
          castShadow
        >
          <meshStandardMaterial
            color={config.front}
            metalness={0.15}
            roughness={0.35}
            emissive={config.front}
            emissiveIntensity={0.08}
          />
        </RoundedBox>

        {/* Top face tint overlay */}
        <mesh position={[0, half + 0.002, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[charSize * 0.88, charSize * 0.88]} />
          <meshStandardMaterial color={config.top} metalness={0.1} roughness={0.4} />
        </mesh>

        {/* Bottom face tint */}
        <mesh position={[0, -half - 0.002, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <planeGeometry args={[charSize * 0.88, charSize * 0.88]} />
          <meshStandardMaterial color={config.bottom} metalness={0.1} roughness={0.5} />
        </mesh>

        {/* Left face tint */}
        <mesh position={[-half - 0.002, 0, 0]} rotation={[0, -Math.PI / 2, 0]}>
          <planeGeometry args={[charSize * 0.88, charSize * 0.88]} />
          <meshStandardMaterial color={config.left} metalness={0.1} roughness={0.4} />
        </mesh>

        {/* Right face tint */}
        <mesh position={[half + 0.002, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
          <planeGeometry args={[charSize * 0.88, charSize * 0.88]} />
          <meshStandardMaterial color={config.right} metalness={0.1} roughness={0.4} />
        </mesh>

        {/* Back face tint + symbol */}
        <mesh position={[0, 0, -half - 0.002]} rotation={[0, Math.PI, 0]}>
          <planeGeometry args={[charSize * 0.88, charSize * 0.88]} />
          <meshStandardMaterial color={config.back} metalness={0.1} roughness={0.5} />
        </mesh>
        <Text
          position={[0, 0, -half - 0.005]}
          rotation={[0, Math.PI, 0]}
          fontSize={charSize * 0.35}
          color="white"
          anchorX="center"
          anchorY="middle"
          fillOpacity={0.3}
        >
          {config.backSymbol}
        </Text>

        {/* ----- FLAT FACE (flush on front surface) ----- */}
        {/* Left eye */}
        <mesh ref={leftEyeRef} position={[-eyeSpacing, eyeY, faceZ]}>
          <planeGeometry args={[eyeW, eyeH]} />
          <meshBasicMaterial color="white" />
        </mesh>
        <mesh ref={leftPupilRef} position={[-eyeSpacing, eyeY - eyeH * 0.05, faceZ + 0.001]}>
          <planeGeometry args={[pupilW, pupilH]} />
          <meshBasicMaterial color="#1a1a2e" />
        </mesh>
        <mesh ref={leftShineRef} position={[-eyeSpacing + eyeW * 0.15, eyeY + eyeH * 0.15, faceZ + 0.002]}>
          <circleGeometry args={[shineR, 8]} />
          <meshBasicMaterial color="white" />
        </mesh>

        {/* Right eye */}
        <mesh ref={rightEyeRef} position={[eyeSpacing, eyeY, faceZ]}>
          <planeGeometry args={[eyeW, eyeH]} />
          <meshBasicMaterial color="white" />
        </mesh>
        <mesh ref={rightPupilRef} position={[eyeSpacing, eyeY - eyeH * 0.05, faceZ + 0.001]}>
          <planeGeometry args={[pupilW, pupilH]} />
          <meshBasicMaterial color="#1a1a2e" />
        </mesh>
        <mesh ref={rightShineRef} position={[eyeSpacing + eyeW * 0.15, eyeY + eyeH * 0.15, faceZ + 0.002]}>
          <circleGeometry args={[shineR, 8]} />
          <meshBasicMaterial color="white" />
        </mesh>

        {/* Mouth - flat shape */}
        <mesh geometry={mouthGeometry} position={[0, -charSize * 0.12, faceZ + 0.001]}>
          <meshBasicMaterial color="#2a1a1a" transparent opacity={0.6} side={THREE.DoubleSide} />
        </mesh>

        {/* Blush cheeks */}
        <mesh position={[-eyeSpacing * 1.4, eyeY - eyeH * 0.7, faceZ + 0.001]}>
          <circleGeometry args={[charSize * 0.055, 12]} />
          <meshBasicMaterial color="#ff9999" transparent opacity={0.25} />
        </mesh>
        <mesh position={[eyeSpacing * 1.4, eyeY - eyeH * 0.7, faceZ + 0.001]}>
          <circleGeometry args={[charSize * 0.055, 12]} />
          <meshBasicMaterial color="#ff9999" transparent opacity={0.25} />
        </mesh>

        {/* Props */}
        {props.map(prop => (
          <PropMesh3D key={prop.id} prop={prop} charSize={charSize} />
        ))}

        {/* Shadow */}
        <mesh position={[0, -half + 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[charSize * 0.4, 16]} />
          <meshBasicMaterial color="black" transparent opacity={0.2} />
        </mesh>
      </group>

      {/* Label */}
      {label && (
        <Text
          position={[0, -half - charSize * 0.4, 0]}
          fontSize={0.12}
          color="white"
          anchorX="center"
          anchorY="top"
          outlineWidth={0.01}
          outlineColor="black"
        >
          {label}
        </Text>
      )}
    </group>
  );
}

export { CHARACTER_FACE_COLORS } from './constants';
